import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { type User } from "@/types/User";
import axios from "@/utils/axios";
import type { InvoiceInformation, Subscription } from "@/types/Subscription";

export const useMeStore = defineStore("me_store", () => {
    const me = ref<User | null>();
    const endpoint = "/api/v1/me";
    const is_authenticated = ref<boolean>(false);


    const fetch_me = async () => {
        try {
            const response = await axios.get(endpoint);
            me.value = response.data;
            is_authenticated.value = true;

        } catch (error) {
            is_authenticated.value = false;
        }
    }

    const subscriptions = computed(() => {
        return me.value?.subscriptions;
    });

    const paid_subscription = computed(() => {
        return subscriptions.value?.find(subscription => subscription.plan.subscription_type === 'standard' && subscription.is_active || subscription.is_upcoming);
    });

    const upcoming_subscription = computed(() => {
        return subscriptions.value?.find(subscription => subscription.is_upcoming);
    });


    const has_running_trial = computed(() => {
        if (paid_subscription.value) return false;
        return subscriptions.value?.find(subscription => subscription.plan.subscription_type === 'trial' && subscription.is_active);
    });

    const trial_expiration_days = computed(() => {
        const trial_subscription = subscriptions.value?.find(subscription => subscription.plan.subscription_type === 'trial');
        if (!trial_subscription) return 0;
        return trial_subscription?.days_left;
    });

    const update_me = async (data: { email: string, workplace: string, profession: string, phone: string, ehr: string }) => {
        const response = await axios.put(endpoint, data);
        me.value = response.data;
    }


    const current_payment_system = computed(() => {
        if (paid_subscription.value) return paid_subscription.value.payment_system;
        if (upcoming_subscription.value) return upcoming_subscription.value.payment_system;
        else return 'free';
    })

    const next_billing_date = computed(() => {
        const subscription = paid_subscription.value;
        if (!subscription) return null;

        if (subscription.is_upcoming) {
            return subscription.started_at;
        }
        return subscription.valid_until;
    });


    const create_invoice_subscription = async (payload: InvoiceInformation) => {
        const response = await axios.post('/api/v1/me/subscriptions', {
            information: { ...payload, country: "Norway" }
        });

        const subscription: Subscription = response.data;

        me.value!.subscriptions.push(subscription);
        return subscription
    }

    const invoice_subscription = computed(() => {
        return me.value?.subscriptions?.find(subscription => subscription.payment_system === 'manual_invoice' && (subscription.is_active || subscription.is_upcoming)) ?? null;
    })

    const stripe_subscription = computed(() => {
        return me.value?.subscriptions?.find(subscription => subscription.payment_system === 'stripe' && (subscription.is_active || subscription.is_upcoming)) ?? null
    })

    const cancel_subscription = async (subscription_id: string) => {
        await axios.post(`/api/v1/me/subscriptions/${subscription_id}/cancel`);
        const existing_subscription = me.value?.subscriptions?.find(subscription => subscription.id === subscription_id);
        if (existing_subscription) {
            existing_subscription.is_cancelled = true;
        }
    }

    const reactivate = async (subscription_id: string) => {
        await axios.post(`/api/v1/me/subscriptions/${subscription_id}/reactivate`);
        const existing_subscription = me.value?.subscriptions?.find(subscription => subscription.id === subscription_id);
        if (existing_subscription) {
            existing_subscription.is_cancelled = false;
        }
    }


    const trial_message = computed(() => {
        if (trial_expiration_days.value <= 0) {
            return `Din prøveperiode har utløpt.`;
        }

        if (trial_expiration_days.value === 1) {
            return `Din prøveperiode utløper i morgen.`;
        }

        return `Din prøveperiode utløper om ${trial_expiration_days.value} dager.`;
    });



    return {
        is_authenticated,
        me,
        update_me,
        fetch_me,
        subscriptions,
        paid_subscription,
        has_running_trial,
        trial_expiration_days,
        next_billing_date,
        current_payment_system,
        create_invoice_subscription,
        invoice_subscription,
        stripe_subscription,
        cancel_subscription,
        reactivate,
        trial_message
    }
});